import type { PageSectionLayout } from '../services/sectionLayoutService';

export function sanitizeWildcardChar(raw: string): string {
  const text = raw.trim();
  const ch = text.match(/[가-힣]/)?.[0] ?? '';
  return ch;
}

/** 시간표 텍스트 끝 `(X)` 접미사가 편제 와일드카드와 일치하는지 */
export function matchesTrackWildcardSuffix(textValue: string, wildcardChar: string): boolean {
  const wildcard = sanitizeWildcardChar(wildcardChar);
  if (!wildcard) return false;
  const matched = textValue.trim().match(/\((.)\)\s*$/);
  if (!matched) return false;
  return matched[1] === wildcard;
}

/** 와일드카드 접미사 `(X)`를 제거한 시간표 본문. 일치하지 않으면 null */
export function stripTrackWildcardSuffix(textValue: string, wildcardChar: string): string | null {
  const wildcard = sanitizeWildcardChar(wildcardChar);
  if (!wildcard) return null;
  const raw = textValue.trim();
  const matched = raw.match(/^(.+)\((.)\)\s*$/);
  if (!matched || matched[2] !== wildcard) return null;
  return matched[1].trim();
}

export function buildWildcardCharsByGrade(
  sections: ReadonlyArray<PageSectionLayout>
): Record<string, Set<string>> {
  const byGrade: Record<string, Set<string>> = {};
  sections.forEach((section) => {
    section.gradeIds.forEach((gradeId) => {
      const set = byGrade[gradeId] ?? new Set<string>();
      (section.trackColumns ?? []).forEach((column) => {
        const wildcard = sanitizeWildcardChar(column.wildcardChar ?? '');
        if (wildcard) set.add(wildcard);
      });
      if (set.size > 0) byGrade[gradeId] = set;
    });
  });
  return byGrade;
}

export function isTimetableTextMatchedByRegisteredWildcard(
  text: string,
  wildcardChars: ReadonlySet<string> | readonly string[]
): boolean {
  const raw = text.trim();
  if (!raw) return false;
  const chars = Array.from(wildcardChars instanceof Set ? wildcardChars : wildcardChars);
  return chars.some((wildcardChar: string) => matchesTrackWildcardSuffix(raw, wildcardChar));
}

/** 편제/기준 시수에 등록되지 않은 시간표명이면 true (빈 값·등록 목록 없음은 false) */
export function isUnregisteredTimetableText(
  raw: string,
  registeredNames: ReadonlySet<string> | readonly string[],
  wildcardChars?: ReadonlySet<string> | readonly string[]
): boolean {
  const value = (raw ?? '').trim();
  if (!value) return false;
  const set = registeredNames instanceof Set ? registeredNames : new Set(registeredNames);
  if (set.size === 0) return false;
  if (set.has(value)) return false;
  if (wildcardChars) {
    const wildcards = wildcardChars instanceof Set ? wildcardChars : new Set(wildcardChars);
    if (wildcards.size > 0 && isTimetableTextMatchedByRegisteredWildcard(value, wildcards)) return false;
  }
  return true;
}
